import { QueryHookOptions, useMutation, useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { isNullish } from 'remeda';
import {
  CREATE_ONCALL_MANAGEMENT_SCHEDULE_MUTATION,
  DELETE_ONCALL_MANAGEMENT_SCHEDULE_MUTATION,
  GET_ONCALL_MANAGEMENT_SCHEDULE_QUERY,
  UPDATE_ONCALL_MANAGEMENT_SCHEDULE_MUTATION,
} from 'src/graphql/entities/signal-oncall-schedule/signal-oncall-schedule.operations';
import {
  DeleteRecurrence,
  EditRecurrence,
  GetOncallManagementScheduleQuery,
  OncallManagementScheduleDataInput,
  OncallManagementScheduleMutationRecord,
  OncallManagementScheduleRecord,
  RecurrenceOptions,
} from 'src/graphql/entities/signal-oncall-schedule/signal-oncall-schedule.types';
import {
  getLimitedRecurrence,
  getRecurrenceUntil,
} from 'src/pages/Settings/signal-oncall-schedule/helpers/events-data-mappers';
import {
  getEditedOccurrenceStartTime,
  getNextRecurrenceStartTime,
} from 'src/pages/Settings/signal-oncall-schedule/helpers/recurrence';
import { useNotifications } from 'src/utils/hooks';
import { useCacheManager } from 'src/utils/hooks/use-cache-manager';

const INITIAL_WINDOW_PAST_MONTHS = 3;
const INITIAL_WINDOW_FUTURE_MONTHS = 3;
const WINDOW_EXPANSION_MONTHS = 3;
const WINDOW_EXPANSION_THRESHOLD_DAYS = 30;

type QueryWindow = {
  start: string;
  end: string;
};

/**
 * Carries a schedule's condition filter over to the events produced when a recurring series is
 * split or truncated. Legacy rows may hold an empty condition group, which the backend rejects as
 * unusable, so those are normalized away instead of being copied forward.
 */
const serializeScheduleConditions = (
  conditions: OncallManagementScheduleRecord['conditions'],
): string | undefined =>
  conditions?.conditionsElements?.length ? JSON.stringify(conditions) : undefined;

export const useSignalOnCallScheduleEvents = (
  partnerId?: number | null,
  options?: QueryHookOptions<GetOncallManagementScheduleQuery>,
) => {
  const initialWindow = useMemo((): QueryWindow => {
    const now = dayjs();
    return {
      start: now.subtract(INITIAL_WINDOW_PAST_MONTHS, 'month').startOf('day').toISOString(),
      end: now.add(INITIAL_WINDOW_FUTURE_MONTHS, 'month').endOf('day').toISOString(),
    };
  }, []);

  const windowRef = useRef<QueryWindow>(initialWindow);

  useEffect(() => {
    windowRef.current = initialWindow;
  }, [initialWindow]);

  const buildWindowFilters = useCallback(
    (window: QueryWindow) => {
      const partnerValue = String(partnerId);
      const oneTimeConditions = [
        { attribute: 'partnerId', operator: '=', value: partnerValue },
        { attribute: 'recurrence', operator: '=', value: RecurrenceOptions.Once },
        { attribute: 'startTime', operator: '<=', value: window.end },
        { attribute: 'endTime', operator: '>=', value: window.start },
      ];

      const recurringWithUntilConditions = [
        {
          attribute: 'partnerId',
          operator: '=',
          value: partnerValue,
          logicalOperator: 'or',
        },
        { attribute: 'recurrence', operator: '!=', value: RecurrenceOptions.Once },
        { attribute: 'startTime', operator: '<=', value: window.end },
        { attribute: 'recurrenceUntil', operator: '>=', value: window.start },
      ];

      const recurringWithoutUntilConditions = [
        {
          attribute: 'partnerId',
          operator: '=',
          value: partnerValue,
          logicalOperator: 'or',
        },
        { attribute: 'recurrence', operator: '!=', value: RecurrenceOptions.Once },
        { attribute: 'startTime', operator: '<=', value: window.end },
        { attribute: 'recurrenceUntil', operator: 'is_null', value: '' },
      ];

      return [
        ...oneTimeConditions,
        ...recurringWithUntilConditions,
        ...recurringWithoutUntilConditions,
      ];
    },
    [partnerId],
  );

  const useQueryResult = useQuery<GetOncallManagementScheduleQuery>(
    GET_ONCALL_MANAGEMENT_SCHEDULE_QUERY,
    {
      ...options,
      skip: isNullish(partnerId),
      variables: {
        params: {
          filters: buildWindowFilters(initialWindow),
        },
      },
    },
  );

  const { data, fetchMore, loading, ...rest } = useQueryResult;

  const cacheManager = useCacheManager(GET_ONCALL_MANAGEMENT_SCHEDULE_QUERY, useQueryResult);

  const records = useMemo(
    () => data?.getOncallManagementSchedule.records || [],
    [data?.getOncallManagementSchedule.records],
  );

  const loadMore = useCallback(
    async (selectedDate: Date) => {
      if (isNullish(partnerId)) {
        return;
      }

      const currentWindow = windowRef.current;
      const selected = dayjs(selectedDate);

      const windowStart = dayjs(currentWindow.start);
      const windowEnd = dayjs(currentWindow.end);
      let nextStart = currentWindow.start;
      let nextEnd = currentWindow.end;
      let fetchPastWindow: QueryWindow | null = null;
      let fetchFutureWindow: QueryWindow | null = null;

      if (selected.isBefore(windowStart.add(WINDOW_EXPANSION_THRESHOLD_DAYS, 'day'))) {
        let expandedStart = windowStart;
        while (selected.isBefore(expandedStart.add(WINDOW_EXPANSION_THRESHOLD_DAYS, 'day'))) {
          expandedStart = expandedStart
            .subtract(WINDOW_EXPANSION_MONTHS, 'month')
            .startOf('day');
        }
        nextStart = expandedStart.toISOString();
        fetchPastWindow = {
          start: nextStart,
          end: windowStart.subtract(1, 'millisecond').toISOString(),
        };
      }

      if (selected.isAfter(windowEnd.subtract(WINDOW_EXPANSION_THRESHOLD_DAYS, 'day'))) {
        let expandedEnd = windowEnd;
        while (
          selected.isAfter(expandedEnd.subtract(WINDOW_EXPANSION_THRESHOLD_DAYS, 'day'))
        ) {
          expandedEnd = expandedEnd.add(WINDOW_EXPANSION_MONTHS, 'month').endOf('day');
        }
        nextEnd = expandedEnd.toISOString();
        fetchFutureWindow = {
          start: windowEnd.add(1, 'millisecond').toISOString(),
          end: nextEnd,
        };
      }

      if (nextStart === currentWindow.start && nextEnd === currentWindow.end) {
        return;
      }

      const nextWindow = { start: nextStart, end: nextEnd };
      windowRef.current = nextWindow;

      const updateQuery = (
        previous: GetOncallManagementScheduleQuery,
        { fetchMoreResult }: { fetchMoreResult?: GetOncallManagementScheduleQuery },
      ): GetOncallManagementScheduleQuery => {
        if (!fetchMoreResult?.getOncallManagementSchedule?.records) {
          return previous;
        }

        const combined = [
          ...previous.getOncallManagementSchedule.records,
          ...fetchMoreResult.getOncallManagementSchedule.records,
        ];

        const uniqueBySchedule = new Map(
          combined.map((record) => [record.oncallManagementScheduleId, record]),
        );

        return {
          getOncallManagementSchedule: {
            ...previous.getOncallManagementSchedule,
            records: Array.from(uniqueBySchedule.values()),
          },
        };
      };

      const fetchSlice = async (window: QueryWindow) => {
        await fetchMore({
          variables: {
            params: {
              filters: buildWindowFilters(window),
            },
          },
          updateQuery,
        });
      };

      if (fetchPastWindow) {
        await fetchSlice(fetchPastWindow);
      }

      if (fetchFutureWindow) {
        await fetchSlice(fetchFutureWindow);
      }
    },
    [buildWindowFilters, fetchMore, partnerId],
  );

  return useMemo(
    () => ({
      isLoading: loading,
      isLoaded: data?.getOncallManagementSchedule.records !== undefined,
      records,
      cacheManager,
      loadMore,
      ...rest,
    }),
    [
      cacheManager,
      data?.getOncallManagementSchedule.records,
      loading,
      loadMore,
      records,
      rest,
    ],
  );
};

const useUpdateEvent = ({ partnerId }: { partnerId: number }) => {
  const { showErrorAlert } = useNotifications();

  const [updateEventMutation, { loading: isLoading }] = useMutation<{
    updateOncallManagementSchedule: OncallManagementScheduleMutationRecord[];
  }>(UPDATE_ONCALL_MANAGEMENT_SCHEDULE_MUTATION, {
    onError: (error) => {
      showErrorAlert('Something went wrong updating schedule event, please try again');
      throw new Error(error.message);
    },
  });

  const updateEvent = useCallback(
    async (event: OncallManagementScheduleDataInput) => {
      const { data } = await updateEventMutation({
        variables: {
          conditions: [
            {
              attribute: 'partnerId',
              operator: '=',
              value: String(partnerId),
            },
            {
              attribute: 'oncallManagementScheduleId',
              operator: '=',
              value: String(event.oncallManagementScheduleId),
            },
          ],
          data: event,
        },
      });

      return data?.updateOncallManagementSchedule?.[0];
    },
    [partnerId, updateEventMutation],
  );

  return useMemo(() => {
    return {
      isLoading,
      updateEvent,
    };
  }, [updateEvent, isLoading]);
};

const useDeleteEvent = () => {
  const { showErrorAlert } = useNotifications();

  const [deleteEventMutation, { loading: isLoading }] = useMutation<{
    deleteOncallManagementSchedule: OncallManagementScheduleMutationRecord[];
  }>(DELETE_ONCALL_MANAGEMENT_SCHEDULE_MUTATION, {
    onError: (error) => {
      showErrorAlert('Something went wrong deleting schedule event, please try again');
      throw new Error(error.message);
    },
  });

  const deleteEvent = useCallback(
    async (eventId: string) => {
      const { data } = await deleteEventMutation({
        variables: {
          conditions: [
            {
              attribute: 'oncallManagementScheduleId',
              operator: '=',
              value: eventId,
            },
          ],
        },
      });

      return data?.deleteOncallManagementSchedule?.[0];
    },
    [deleteEventMutation],
  );

  return useMemo(() => {
    return {
      isLoading,
      deleteEvent,
    };
  }, [deleteEvent, isLoading]);
};

export const useCreateSignalOnCallScheduleEvent = () => {
  const { showErrorAlert } = useNotifications();

  const [createOncallManagementScheduleMutation, { loading: isLoading }] = useMutation<{
    createOncallManagementSchedule: OncallManagementScheduleMutationRecord[];
  }>(CREATE_ONCALL_MANAGEMENT_SCHEDULE_MUTATION, {
    onError: (error) => {
      showErrorAlert('Something went wrong creating schedule event, please try again');
      throw new Error(error.message);
    },
  });

  const createOnCallScheduleEvent = useCallback(
    async (event: Omit<OncallManagementScheduleDataInput, 'oncallManagementScheduleId'>) => {
      const { data } = await createOncallManagementScheduleMutation({
        variables: {
          data: event,
        },
      });

      return data?.createOncallManagementSchedule?.[0];
    },
    [createOncallManagementScheduleMutation],
  );

  return useMemo(() => {
    return {
      isLoading,
      createOnCallScheduleEvent,
    };
  }, [createOnCallScheduleEvent, isLoading]);
};

export const useDeleteSignalOnCallScheduleEvent = ({
  partnerId,
  partnerTimezone,
  initialEvent,
}: {
  partnerId: number;
  partnerTimezone: string;
  initialEvent: OncallManagementScheduleRecord | undefined;
}) => {
  const { deleteEvent, isLoading: isDeleting } = useDeleteEvent();
  const { updateEvent, isLoading: isUpdating } = useUpdateEvent({
    partnerId,
  });
  const { createOnCallScheduleEvent, isLoading: isCreating } =
    useCreateSignalOnCallScheduleEvent();

  const isLoading = isDeleting || isUpdating || isCreating;

  const deleteOnCallScheduleEvent = useCallback(
    async (
      data: Pick<
        OncallManagementScheduleDataInput,
        'oncallManagementScheduleId' | 'startTime' | 'recurrence'
      > &
        Partial<Pick<OncallManagementScheduleDataInput, 'endTime' | 'conditions'>>,
      deleteMode?: DeleteRecurrence,
    ) => {
      let updatedEvent: OncallManagementScheduleMutationRecord | undefined;
      let createdEvent: OncallManagementScheduleMutationRecord | undefined;

      if (!initialEvent) {
        // event not found, nothing to delete
        return null;
      }

      const isDeletingFirstEventInTheSequence = dayjs(data.startTime)
        .tz(partnerTimezone)
        .isSame(dayjs(initialEvent.startTime).tz(partnerTimezone), 'day');
      const isRecurring =
        data.recurrence && (data.recurrence as RecurrenceOptions) !== RecurrenceOptions.Once;

      if (!isRecurring) {
        await deleteEvent(data.oncallManagementScheduleId);
        return {
          deletedEventId: data.oncallManagementScheduleId,
          updatedEvent: undefined,
          createdEvent: undefined,
        };
      }

      const resolvedDeleteMode = deleteMode ?? DeleteRecurrence.This;

      if (resolvedDeleteMode === DeleteRecurrence.This) {
        if (isDeletingFirstEventInTheSequence) {
          const nextStartTime = getNextRecurrenceStartTime({
            recurrence: initialEvent.recurrence,
            dtstart: initialEvent.startTime,
            after: initialEvent.startTime,
            timeZone: partnerTimezone,
          });

          if (!nextStartTime) {
            await deleteEvent(data.oncallManagementScheduleId);
            return {
              deletedEventId: data.oncallManagementScheduleId,
              updatedEvent: undefined,
              createdEvent: undefined,
            };
          }

          const initialEventLength = dayjs(initialEvent.endTime).diff(
            initialEvent.startTime,
            'ms',
          );
          const nextEndTime = dayjs(nextStartTime).add(initialEventLength, 'ms').toISOString();

          updatedEvent = await updateEvent({
            oncallManagementScheduleId: initialEvent.oncallManagementScheduleId,
            partnerId: initialEvent.partner.partnerId,
            primaryAgentId: initialEvent.primaryAgent.id,
            secondaryAgentId: initialEvent.secondaryAgent
              ? initialEvent.secondaryAgent.id
              : null,
            startTime: nextStartTime,
            endTime: nextEndTime,
            recurrence: initialEvent.recurrence,
            recurrenceUntil: getRecurrenceUntil(initialEvent.recurrence),
            conditions: serializeScheduleConditions(initialEvent.conditions),
          });

          return {
            deletedEventId: undefined,
            updatedEvent,
            createdEvent: undefined,
          };
        }

        const truncatedRecurrence = getLimitedRecurrence(
          initialEvent.recurrence,
          dayjs(data.startTime)
            .subtract(1, 'day')
            .tz(partnerTimezone)
            .endOf('day')
            .toISOString(),
        );

        updatedEvent = await updateEvent({
          oncallManagementScheduleId: initialEvent.oncallManagementScheduleId,
          partnerId: initialEvent.partner.partnerId,
          primaryAgentId: initialEvent.primaryAgent.id,
          secondaryAgentId: initialEvent.secondaryAgent
            ? initialEvent.secondaryAgent.id
            : null,
          startTime: initialEvent.startTime,
          endTime: initialEvent.endTime,
          recurrence: truncatedRecurrence,
          recurrenceUntil: getRecurrenceUntil(truncatedRecurrence),
          conditions: serializeScheduleConditions(initialEvent.conditions),
        });

        const initialEventLength = dayjs(initialEvent.endTime).diff(
          initialEvent.startTime,
          'ms',
        );
        const nextOccurrenceDateStartTime = getNextRecurrenceStartTime({
          recurrence: initialEvent.recurrence,
          dtstart: initialEvent.startTime,
          after: data.startTime,
          timeZone: partnerTimezone,
        });

        if (!nextOccurrenceDateStartTime) {
          return {
            deletedEventId: undefined,
            updatedEvent,
            createdEvent: undefined,
          };
        }

        const nextOccurrenceDateEndTime = dayjs(nextOccurrenceDateStartTime)
          .add(initialEventLength, 'ms')
          .toISOString();

        createdEvent = await createOnCallScheduleEvent({
          partnerId: initialEvent.partner.partnerId,
          primaryAgentId: initialEvent.primaryAgent.id,
          secondaryAgentId: initialEvent.secondaryAgent
            ? initialEvent.secondaryAgent.id
            : null,
          startTime: nextOccurrenceDateStartTime,
          endTime: nextOccurrenceDateEndTime,
          recurrence: initialEvent.recurrence,
          recurrenceUntil: getRecurrenceUntil(initialEvent.recurrence),
          conditions: serializeScheduleConditions(initialEvent.conditions),
        });

        return {
          deletedEventId: undefined,
          updatedEvent,
          createdEvent,
        };
      }

      if (resolvedDeleteMode === DeleteRecurrence.ThisAndFollowing) {
        if (isDeletingFirstEventInTheSequence) {
          await deleteEvent(data.oncallManagementScheduleId);
          return {
            deletedEventId: data.oncallManagementScheduleId,
            updatedEvent: undefined,
            createdEvent: undefined,
          };
        }

        const limitedRecurrence = getLimitedRecurrence(
          initialEvent.recurrence,
          dayjs(data.startTime)
            .subtract(1, 'day')
            .tz(partnerTimezone)
            .endOf('day')
            .toISOString(),
        );

        updatedEvent = await updateEvent({
          oncallManagementScheduleId: initialEvent.oncallManagementScheduleId,
          partnerId: initialEvent.partner.partnerId,
          primaryAgentId: initialEvent.primaryAgent.id,
          secondaryAgentId: initialEvent.secondaryAgent
            ? initialEvent.secondaryAgent.id
            : null,
          startTime: initialEvent.startTime,
          endTime: initialEvent.endTime,
          recurrence: limitedRecurrence,
          recurrenceUntil: getRecurrenceUntil(limitedRecurrence),
          conditions: serializeScheduleConditions(initialEvent.conditions),
        });

        return {
          deletedEventId: undefined,
          updatedEvent,
          createdEvent: undefined,
        };
      }

      return {
        deletedEventId: data.oncallManagementScheduleId,
        updatedEvent: undefined,
        createdEvent: undefined,
      };
    },
    [createOnCallScheduleEvent, deleteEvent, initialEvent, partnerTimezone, updateEvent],
  );

  return useMemo(() => {
    return {
      isLoading,
      deleteOnCallScheduleEvent,
    };
  }, [deleteOnCallScheduleEvent, isLoading]);
};

type RecurringEditResult = {
  updatedEvent?: OncallManagementScheduleMutationRecord;
  createdEvents: OncallManagementScheduleMutationRecord[];
  deletedEventId?: string;
};

export const useUpdateSignalOnCallScheduleEvent = ({
  partnerId,
  partnerTimezone,
  initialEvent,
}: {
  partnerId: number;
  partnerTimezone: string;
  initialEvent: OncallManagementScheduleRecord | undefined;
}) => {
  const { deleteEvent, isLoading: isDeleting } = useDeleteEvent();
  const { updateEvent, isLoading: isUpdating } = useUpdateEvent({
    partnerId,
  });
  const { createOnCallScheduleEvent: createSignalOnCallEvent, isLoading: isCreating } =
    useCreateSignalOnCallScheduleEvent();

  const isLoading = isUpdating || isDeleting || isCreating;

  const updateOnCallScheduleEvent = useCallback(
    async (
      updateData: OncallManagementScheduleDataInput,
      editType?: EditRecurrence,
    ): Promise<RecurringEditResult | null> => {
      const { oncallManagementScheduleId: eventId, ...createData } = updateData;

      /** *
        Non-recurring event
      */

      if (!editType) {
        return { updatedEvent: await updateEvent(updateData), createdEvents: [] };
      }

      if (!initialEvent) {
        // event not found, nothing to update
        return null;
      }

      /** *
        Recurring event
      */

      let updatedEvent: OncallManagementScheduleMutationRecord | undefined;
      const createdEvents: OncallManagementScheduleMutationRecord[] = [];
      let deletedEventId: string | undefined;

      const initialEventLength = dayjs(initialEvent.endTime).diff(
        initialEvent.startTime,
        'ms',
      );
      const initialConditions = serializeScheduleConditions(initialEvent.conditions);

      const editedOccurrenceStartTime = getEditedOccurrenceStartTime(
        initialEvent.startTime,
        updateData.startTime,
        partnerTimezone,
      );

      const isUpdatingFirstEventInTheSequence = dayjs(updateData.startTime)
        .tz(partnerTimezone)
        .isSame(dayjs(initialEvent.startTime).tz(partnerTimezone), 'day');

      /*
        Find date where initial recurrence of events should
        stop (a day before the new event's start time).
      */
      const lastOccurenceDateForPreviousSequence = isUpdatingFirstEventInTheSequence
        ? null
        : dayjs(updateData.startTime)
            .subtract(1, 'day')
            .tz(partnerTimezone)
            .endOf('day')
            .toISOString();

      // First we set a limit to previous sequence of events, if it exists.
      if (!isUpdatingFirstEventInTheSequence && lastOccurenceDateForPreviousSequence) {
        const limitedRecurrence = getLimitedRecurrence(
          initialEvent.recurrence,
          lastOccurenceDateForPreviousSequence,
        );
        // Update original event to end on the date before new one.
        updatedEvent = await updateEvent({
          oncallManagementScheduleId: initialEvent.oncallManagementScheduleId,
          partnerId: initialEvent.partner.partnerId,
          primaryAgentId: initialEvent.primaryAgent.id,
          secondaryAgentId: initialEvent.secondaryAgent
            ? initialEvent.secondaryAgent.id
            : null,
          startTime: initialEvent.startTime,
          endTime: initialEvent.endTime,
          recurrence: limitedRecurrence,
          recurrenceUntil: getRecurrenceUntil(limitedRecurrence),
          conditions: initialConditions,
        });
      }

      // Then create a new recurring sequence starting from the date of new event,
      // if we're updating this and following events.
      if (editType === EditRecurrence.ThisAndFollowing) {
        if (isUpdatingFirstEventInTheSequence && eventId) {
          await deleteEvent(eventId);
          deletedEventId = eventId;
        }
        const createdSeries = await createSignalOnCallEvent(createData);
        if (createdSeries) {
          createdEvents.push(createdSeries);
        }
      } else if (editType === EditRecurrence.This) {
        // Continue the original sequence from its next real occurrence (matching
        // the delete flow) instead of the next calendar day, so the recurrence
        // phase is preserved.
        const nextOccurrenceDateStartTime = getNextRecurrenceStartTime({
          recurrence: initialEvent.recurrence,
          dtstart: initialEvent.startTime,
          after: isUpdatingFirstEventInTheSequence
            ? initialEvent.startTime
            : editedOccurrenceStartTime,
          timeZone: partnerTimezone,
        });

        if (nextOccurrenceDateStartTime) {
          const nextOccurrenceDateEndTime = dayjs(nextOccurrenceDateStartTime)
            .add(initialEventLength, 'ms')
            .toISOString();

          const continuationSchedule = {
            partnerId: initialEvent.partner.partnerId,
            primaryAgentId: initialEvent.primaryAgent.id,
            secondaryAgentId: initialEvent.secondaryAgent
              ? initialEvent.secondaryAgent.id
              : null,
            recurrence: initialEvent.recurrence,
            recurrenceUntil: getRecurrenceUntil(initialEvent.recurrence),
            startTime: nextOccurrenceDateStartTime,
            endTime: nextOccurrenceDateEndTime,
            conditions: initialConditions,
          };

          // If we're editing the first event in the sequence we shift the
          // existing schedule to its next occurrence; otherwise we "continue"
          // the interrupted sequence with a fresh schedule.
          if (isUpdatingFirstEventInTheSequence) {
            updatedEvent = await updateEvent({
              oncallManagementScheduleId: initialEvent.oncallManagementScheduleId,
              ...continuationSchedule,
            });
          } else {
            const continuation = await createSignalOnCallEvent(continuationSchedule);
            if (continuation) {
              createdEvents.push(continuation);
            }
          }
        } else if (isUpdatingFirstEventInTheSequence && eventId) {
          // No further occurrences and we edited the only/first event — remove
          // the original recurring row; the one-time override below replaces it.
          await deleteEvent(eventId);
          deletedEventId = eventId;
        }

        // And create a one-time event with new parameters.
        createData.recurrence = RecurrenceOptions.Once;
        createData.recurrenceUntil = getRecurrenceUntil(createData.recurrence);
        const onceOverride = await createSignalOnCallEvent(createData);
        if (onceOverride) {
          createdEvents.push(onceOverride);
        }
      }

      return { updatedEvent, createdEvents, deletedEventId };
    },
    [createSignalOnCallEvent, deleteEvent, initialEvent, partnerTimezone, updateEvent],
  );

  return useMemo(() => {
    return {
      isLoading,
      updateOnCallScheduleEvent,
    };
  }, [updateOnCallScheduleEvent, isLoading]);
};
